'use client'

import React from 'react'

import { ImageGalleryStripCarousel } from '@/components/Gallery/ImageGalleryStripCarousel'
import { containerClassName } from '@/components/Container'
import { HeadingBodySplitSection } from '@/components/HeadingBodySplitSection'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import type { CommunityOperatorSectionData } from '@/lib/mock/communityOperator'
import { cn } from '@/utilities/ui'

type CommunityOperatorSectionProps = {
  offsetTop?: boolean
  section: CommunityOperatorSectionData
}

export const CommunityOperatorSection: React.FC<CommunityOperatorSectionProps> = ({
  offsetTop = false,
  section,
}) => {
  const stats = section.stats ?? []
  const hasStats = stats.length > 0

  return (
    <section
      aria-labelledby="community-operator-heading"
      className="isolate overflow-x-clip bg-cream "
    >
      <div
        className={cn(
          containerClassName,
          'pb-0',
          offsetTop ? 'pt-[16rem] sm:pt-[18rem] lg:pt-[22rem]' : 'pt-12 lg:pt-20',
        )}
      >
        {hasStats ? (
          <div className="grid min-w-0 gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,30rem)] lg:items-start lg:gap-16">
            <div className="min-w-0">
              <ColorTrailText
                as="h2"
                className="min-w-0 break-words font-display text-[2.5rem] font-semibold leading-tight tracking-[-0.04em] text-bluetext md:text-[3.5rem] lg:text-balance lg:text-[4rem]"
                id="community-operator-heading"
                segments={[
                  {
                    finalColor: 'var(--brand-bluetext)',
                    text: section.titleBeforeHighlight,
                    trail: false,
                  },
                  {
                    finalColor: 'var(--brand-dark-green)',
                    text: section.titleHighlight,
                  },
                ]}
              />

              <dl className="mt-8 flex flex-wrap items-start gap-6 sm:gap-8">
                {stats.map((stat, index) => (
                  <React.Fragment key={stat.label}>
                    {index > 0 ? (
                      <div aria-hidden className="h-14 w-px shrink-0 bg-black/10" />
                    ) : null}
                    <div className="flex w-fit max-w-[10rem] flex-col-reverse">
                      <dt className="mt-1 font-outfit text-base leading-tight text-bluetext/80">
                        {stat.label}
                      </dt>
                      <dd className="font-display text-[2rem] font-medium leading-none text-bluetext">
                        {stat.value}
                      </dd>
                    </div>
                  </React.Fragment>
                ))}
              </dl>
            </div>

            <div className="min-w-0 font-outfit text-base leading-relaxed break-words text-bluetext/95">
              {section.bodyParagraphs.map((paragraph, index) => (
                <p key={index} className={index > 0 ? 'mt-4' : undefined}>
                  {paragraph}
                </p>
              ))}
            </div>
          </div>
        ) : (
          <HeadingBodySplitSection
            bodyParagraphs={section.bodyParagraphs}
            headingId="community-operator-heading"
            titleBeforeHighlight={section.titleBeforeHighlight}
            titleHighlight={section.titleHighlight}
          />
        )}
      </div>

      <ImageGalleryStripCarousel
        ariaLabel={section.galleryAriaLabel}
        bgColor="bg-cream"
        carouselNextLabel={section.carouselNext}
        carouselPreviousLabel={section.carouselPrevious}
        className="mt-10 lg:mt-12"
        pullUp={false}
        slides={section.slides}
      />
    </section>
  )
}
